import React, { useMemo } from 'react';
import { Card, Space, Divider, Typography, Row, Col, Table } from 'antd';
import PnL from "../../../Components/PnL";
import { useEnsureTickersLoaded } from "../../../hooks/useEnsureTickersLoaded";
import NivoPieChart from "../../../charts/nivo/NivoPieChart";
import { sampleColors } from "../../../utils/colors";
import {colorTableRow} from "../../Comparison/common";
import {stringMixin} from "../../../utils/strings";
import {DoubleLine} from "../../../Components/Labels";

const formatDataForPieChart = (snapshot) => {
    let data = [];
    for (let k in snapshot) {
        if (snapshot[k].wgt > 0) {
            data.push({ id: k, value: (snapshot[k].wgt * 100).toFixed(2) });
        }
    }
    data.sort((a, b) => b.value - a.value);
    return data.length > 8 ? data.slice(0, 8) : data;
};

const BlotterSnapshot = ({ row, extraHeaders, previous, colorMap, betaRow }) => {
    const [date, portfolioValue, snapshot, benchmarkValue, ...extraTickers] = row;
    const pieChartData = formatDataForPieChart(snapshot);
    const snapshotData = Object.entries(snapshot).map(([ticker, details], index) => ({
        key: index,
        ticker,
        weight: details.wgt,
        value: details.val,
    })).sort((a, b) => b.weight - a.weight).map((row, index)=>{
        return {...row, index: index+1}
    });
    console.log('BETA ROW  ', betaRow)

    const filteredSnapshotData = snapshotData.filter((d) => d.weight > 0);
    const uniqueTickers = new Set(filteredSnapshotData.map((d) => d.ticker));
    const { tickers } = useEnsureTickersLoaded(Array.from(uniqueTickers));

    const portfolioBeta = useMemo(()=>{
        return betaRow ? betaRow.data.find(d=>d.id==='portfolio')?.value : null;
    })

    const previousPortfolioValue = previous ? previous[1] : 0;
    const previousBenchmarkValue = previous ? previous[3] : 0;
    const portfolioChange = ((portfolioValue - previousPortfolioValue) / previousPortfolioValue) * 100;
    const benchmarkChange = ((benchmarkValue - previousBenchmarkValue) / previousBenchmarkValue) * 100;


    const columns = [
        {
            title: '',
            dataIndex: 'index',
            key: 'index',
            align: 'center',
        },
        {
            title: '',
            dataIndex: 'ticker',
            key: 'ticker',
            render: (text)=><Typography.Text strong>{text}</Typography.Text>

        },
        {
            title: '',
            dataIndex: 'weight',
            key: 'weight',
            render: (text) => `${(text * 100).toFixed(2)}%`
        },
    ];

    return (
        <Card bordered={false} className={'shadow-sm'}>
            <Row gutter={32}>
                <Col>
                    <Typography.Title level={3}>
                        {date} <Typography.Text strong>Snapshot</Typography.Text>
                    </Typography.Title>
                </Col>
            </Row>
            <Row justify={'start'} gutter={32}>
                <Col>

                    <Space direction={'vertical'}>
                        <Typography.Text strong>Portfolio</Typography.Text>
                        <PnL value={portfolioValue} />
                    </Space>
                </Col>
                <Col>
                    <Space direction={'vertical'}>
                        <Typography.Text strong>Benchmark</Typography.Text>
                        <PnL value={benchmarkValue} />
                    </Space>
                </Col>
                {portfolioBeta && (
                    <Col>
                        <Space direction={'vertical'}>
                            <Typography.Text strong>Beta</Typography.Text>
                            {stringMixin.pct(portfolioBeta)}
                        </Space>
                    </Col>
                )}
            </Row>
            <Row wrap={false} align={'middle'}>
               <Col flex={'0 0 150px'}>
                    <Table
                        className={'flat'}
                        columns={columns}
                        dataSource={filteredSnapshotData}
                        pagination={false}
                        rowKey="ticker"
                        size="small"
                        bordered={false}
                        style={{ width: 300 }}
                        onRow={(record, index)=>{
                            return colorTableRow({...record, color: colorMap[record.ticker]});
                        }}
                    />
                </Col>
                <Col flex={'auto'}>
                    <div style={{ height: 250, width: 250 }}>
                        <NivoPieChart
                            data={pieChartData}
                            legend={false}
                            showLabels={false}
                            radius={0}
                            customColors={colorMap}
                        />
                    </div>
                </Col>
            </Row>
        </Card>
    );
};

const BlotterSnapshots = ({ data, extraDetails }) => {
    const extraHeaders = data[0].slice(3);
    const rows = data.slice(1);

    const betaRows = extraDetails?.betaRows;

    // Step 1: Collect all unique tickers across snapshots
    const uniqueTickers = useMemo(() => {
        const tickersSet = new Set();
        rows.forEach(([date, portfolioValue, snapshot, benchmarkValue, ...extraTickers]) => {
            Object.keys(snapshot).forEach((ticker) => {
                tickersSet.add(ticker);
            });
        });
        return Array.from(tickersSet);
    }, [rows]);

    // Step 2: Assign colors to each unique ticker
    const colorMap = useMemo(() => {
        const colors = sampleColors(uniqueTickers.length);
        let mapping = {};
        uniqueTickers.forEach((ticker, i) => {
            mapping[ticker] = colors[i];
        });
        return mapping;
    }, [uniqueTickers]);

    return (
        <Row justify={'center'} wrap={true} gutter={96}>
            {rows.map((row, index) => (
                <Col flex={'0 0 400px'}>

                    <React.Fragment key={index}>
                            <BlotterSnapshot
                                row={row}
                                extraHeaders={extraHeaders}
                                previous={index > 0 ? rows[index - 1] : null}
                                colorMap={colorMap}
                                betaRow={betaRows ? betaRows[index] : null}
                            />
                        <Divider />
                    </React.Fragment>
                </Col>

            ))}
        </Row>
    );
};

export default BlotterSnapshots;
